import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Edit2,
  Trash2,
  RefreshCw,
  Save,
  X,
  Power,
  PowerOff,
  CreditCard,
  ChevronDown,
  ChevronRight,
  Eye,
  EyeOff,
  Plane,
} from 'lucide-react';
import { mileageCardService } from '../services/mileageCardService';
import {
  MileageCard,
  CreateMileageCardRequest,
  MileageBalance,
  MileageCardCredentials,
  MileagePurchase,
  CreateMileagePurchaseRequest,
  MileageUsage,
  LoyaltyProgram,
  MileagePaymentStatus,
  MileageUsageLeg,
} from '../interfaces/MileageCard';
import { DefaultLoader } from '../components';

const PROGRAMS: { value: LoyaltyProgram; label: string }[] = [
  { value: 'azul', label: 'Azul' },
  { value: 'latam', label: 'LATAM' },
  { value: 'smiles', label: 'Smiles' },
  { value: 'livelo', label: 'Livelo' },
  { value: 'tap', label: 'TAP' },
];

const PROGRAM_LABEL: Record<LoyaltyProgram, string> = {
  azul: 'Azul',
  latam: 'LATAM',
  smiles: 'Smiles',
  livelo: 'Livelo',
  tap: 'TAP',
};

const PAYMENT_STATUS_LABEL: Record<MileagePaymentStatus, string> = {
  pending: 'Pendente',
  paid: 'Pago',
  overdue: 'Atrasado',
};

const PAYMENT_STATUS_COLORS: Record<
  MileagePaymentStatus,
  { bg: string; fg: string }
> = {
  pending: { bg: '#fff7e6', fg: '#d46b08' },
  paid: { bg: '#f6ffed', fg: '#389e0d' },
  overdue: { bg: '#fff2f0', fg: '#cf1322' },
};

const USAGE_LEG_LABEL: Record<MileageUsageLeg, string> = {
  outbound: 'Ida',
  inbound: 'Volta',
  full: 'Ida e Volta',
};

const EMPTY_CARD_FORM: CreateMileageCardRequest = {
  program: 'azul',
  name: '',
  holder_name: '',
  login: '',
  password: '',
  is_active: true,
};

const EMPTY_PURCHASE_FORM: CreateMileagePurchaseRequest = {
  milesPurchased: 0,
  cost: undefined,
  purchasedAt: '',
  paymentDueDate: '',
  paidAt: '',
  paymentStatus: 'pending',
  payWithPix: false,
  pixKey: '',
  notes: '',
};

// ---------- estilos reutilizados ----------
const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid #d9d9d9',
  borderRadius: '6px',
  fontSize: '13px',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '12px',
  fontWeight: 600,
  marginBottom: '4px',
  color: '#555',
};

const primaryBtn: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '8px 16px',
  background: '#1890ff',
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: '13px',
};

const secondaryBtn: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '8px 14px',
  border: '1px solid #d9d9d9',
  borderRadius: '6px',
  background: '#fff',
  cursor: 'pointer',
  fontSize: '13px',
};

const iconBtn: React.CSSProperties = {
  padding: '5px',
  border: '1px solid #d9d9d9',
  borderRadius: '4px',
  background: '#fff',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
};

const thStyle: React.CSSProperties = {
  padding: '8px 16px',
  textAlign: 'left',
  fontWeight: 600,
  color: '#666',
  borderBottom: '1px solid #f0f0f0',
  fontSize: '12px',
};

const formatMiles = (n: number) => Number(n).toLocaleString('pt-BR');

const formatPrice = (price?: number) =>
  price === null || price === undefined
    ? '—'
    : Number(price).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      });

/**
 * Preço médio pago por mil milhas (CPM — custo por milheiro).
 * CPM = custo total / milhas compradas * 1000.
 * Retorna '—' quando não há custo informado ou milhas inválidas.
 */
const formatPricePerThousand = (cost?: number, miles?: number) => {
  if (cost === null || cost === undefined || !miles || miles <= 0) {
    return '—';
  }
  const cpm = (Number(cost) / Number(miles)) * 1000;
  return Number(cpm).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

const formatDate = (d?: string) => {
  if (!d) return '—';
  const [y, m, day] = d.split('T')[0].split('-');
  return `${day}/${m}/${y}`;
};

const MileageCards: React.FC = () => {
  const [cards, setCards] = useState<MileageCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // formulário de cartão
  const [showCardForm, setShowCardForm] = useState(false);
  const [editingCard, setEditingCard] = useState<MileageCard | null>(null);
  const [cardForm, setCardForm] =
    useState<CreateMileageCardRequest>(EMPTY_CARD_FORM);

  // painel expandido
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [balances, setBalances] = useState<Record<string, MileageBalance>>({});
  const [purchases, setPurchases] = useState<Record<string, MileagePurchase[]>>(
    {},
  );
  const [usages, setUsages] = useState<Record<string, MileageUsage[]>>({});
  const [revealed, setRevealed] = useState<
    Record<string, MileageCardCredentials>
  >({});

  // sub-formulários
  const [purchaseForm, setPurchaseForm] =
    useState<CreateMileagePurchaseRequest>(EMPTY_PURCHASE_FORM);
  const [showPurchaseForm, setShowPurchaseForm] = useState<string | null>(null);

  const loadCards = useCallback(async () => {
    try {
      setLoading(true);
      const data = await mileageCardService.getCards();
      setCards(data);
    } catch {
      setError('Erro ao carregar cartões de milhas.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCards();
  }, [loadCards]);

  const loadCardDetails = useCallback(async (cardId: string) => {
    try {
      const [bal, purch, use] = await Promise.all([
        mileageCardService.getBalance(cardId),
        mileageCardService.getPurchases(cardId),
        mileageCardService.getUsages(cardId),
      ]);
      setBalances((p) => ({ ...p, [cardId]: bal }));
      setPurchases((p) => ({ ...p, [cardId]: purch }));
      setUsages((p) => ({ ...p, [cardId]: use }));
    } catch {
      setError('Erro ao carregar detalhes do cartão.');
    }
  }, []);

  const toggleExpand = async (cardId: string) => {
    if (expandedId === cardId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(cardId);
    await loadCardDetails(cardId);
  };

  // ---------- cartão ----------
  const handleCardSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const payload: CreateMileageCardRequest = {
        ...cardForm,
        name: cardForm.name.trim(),
        login: cardForm.login.trim(),
      };
      if (editingCard) {
        // senha vazia na edição = não altera
        if (!payload.password) delete (payload as Partial<CreateMileageCardRequest>).password;
        await mileageCardService.updateCard(editingCard.id, payload);
      } else {
        await mileageCardService.createCard(payload);
      }
      cancelCardForm();
      await loadCards();
    } catch {
      setError('Erro ao salvar cartão de milhas.');
    } finally {
      setSaving(false);
    }
  };

  const handleEditCard = (card: MileageCard) => {
    setEditingCard(card);
    setCardForm({
      program: card.program,
      name: card.name,
      holder_name: card.holder_name ?? '',
      login: card.login,
      password: '',
      is_active: card.is_active,
    });
    setShowCardForm(true);
    setError(null);
  };

  const handleDeleteCard = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja desativar este cartão?')) return;
    try {
      await mileageCardService.deleteCard(id);
      await loadCards();
    } catch {
      setError('Erro ao desativar cartão.');
    }
  };

  const handleToggleActive = async (card: MileageCard) => {
    try {
      await mileageCardService.updateCard(card.id, {
        is_active: !card.is_active,
      });
      await loadCards();
    } catch {
      setError('Erro ao atualizar status do cartão.');
    }
  };

  const handleRevealCredentials = async (cardId: string) => {
    if (revealed[cardId]) {
      setRevealed((p) => {
        const next = { ...p };
        delete next[cardId];
        return next;
      });
      return;
    }
    try {
      const creds = await mileageCardService.getCredentials(cardId);
      setRevealed((p) => ({ ...p, [cardId]: creds }));
    } catch {
      setError('Erro ao revelar credenciais.');
    }
  };

  const cancelCardForm = () => {
    setShowCardForm(false);
    setEditingCard(null);
    setCardForm(EMPTY_CARD_FORM);
    setError(null);
  };

  const handleCardChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value, type } = e.target;
    setCardForm((prev) => ({
      ...prev,
      [name]:
        type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  // ---------- compra ----------
  const handlePurchaseSubmit = async (e: React.FormEvent, cardId: string) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const payload: CreateMileagePurchaseRequest = {
        milesPurchased: Number(purchaseForm.milesPurchased),
        cost:
          purchaseForm.cost === undefined || String(purchaseForm.cost) === ''
            ? undefined
            : Number(purchaseForm.cost),
        purchasedAt: purchaseForm.purchasedAt,
        paymentDueDate: purchaseForm.paymentDueDate || undefined,
        paidAt: purchaseForm.paidAt || undefined,
        paymentStatus: purchaseForm.paymentStatus,
        payWithPix: purchaseForm.payWithPix ?? false,
        pixKey: purchaseForm.payWithPix
          ? purchaseForm.pixKey || undefined
          : undefined,
        notes: purchaseForm.notes || undefined,
      };
      await mileageCardService.createPurchase(cardId, payload);
      setShowPurchaseForm(null);
      setPurchaseForm(EMPTY_PURCHASE_FORM);
      await loadCardDetails(cardId);
    } catch {
      setError('Erro ao registrar compra de milhas.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePurchase = async (purchaseId: string, cardId: string) => {
    if (!window.confirm('Excluir esta compra de milhas?')) return;
    try {
      await mileageCardService.deletePurchase(purchaseId);
      await loadCardDetails(cardId);
    } catch {
      setError('Erro ao excluir compra.');
    }
  };

  if (loading) return <DefaultLoader />;

  return (
    <div style={{ padding: '24px', maxWidth: '1040px' }}>
      {/* Cabeçalho */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <CreditCard size={28} color="#1890ff" />
          <div>
            <h1 style={{ margin: 0, fontSize: '22px', fontWeight: 700 }}>
              Cartões de Milhas
            </h1>
            <p style={{ margin: 0, color: '#888', fontSize: '13px' }}>
              Gerencie contas de programas de fidelidade, compras de milhas e o
              consumo atrelado aos checkouts de aéreo
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={loadCards} style={secondaryBtn}>
            <RefreshCw size={14} />
            Atualizar
          </button>
          <button
            onClick={() => {
              setShowCardForm(true);
              setEditingCard(null);
              setCardForm(EMPTY_CARD_FORM);
              setError(null);
            }}
            style={primaryBtn}
          >
            <Plus size={16} />
            Novo Cartão
          </button>
        </div>
      </div>

      {error && (
        <div
          style={{
            background: '#fff2f0',
            border: '1px solid #ffccc7',
            borderRadius: '6px',
            padding: '12px 16px',
            marginBottom: '16px',
            color: '#a8071a',
            fontSize: '13px',
          }}
        >
          {error}
        </div>
      )}

      {/* Formulário de cartão */}
      {showCardForm && (
        <div
          style={{
            background: '#f8f9fa',
            border: '1px solid #e8e8e8',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '24px',
          }}
        >
          <h3 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: 600 }}>
            {editingCard ? 'Editar Cartão' : 'Novo Cartão de Milhas'}
          </h3>
          <form onSubmit={handleCardSubmit}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '12px',
                marginBottom: '12px',
              }}
            >
              <div>
                <label style={labelStyle}>Programa *</label>
                <select
                  name="program"
                  value={cardForm.program}
                  onChange={handleCardChange}
                  required
                  style={inputStyle}
                >
                  {PROGRAMS.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Nome/Apelido *</label>
                <input
                  name="name"
                  value={cardForm.name}
                  onChange={handleCardChange}
                  required
                  maxLength={150}
                  placeholder="ex: Azul Principal"
                  style={inputStyle}
                />
              </div>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                gap: '12px',
                marginBottom: '12px',
              }}
            >
              <div>
                <label style={labelStyle}>Titular</label>
                <input
                  name="holder_name"
                  value={cardForm.holder_name}
                  onChange={handleCardChange}
                  maxLength={150}
                  placeholder="Nome do titular"
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Login/Usuário *</label>
                <input
                  name="login"
                  value={cardForm.login}
                  onChange={handleCardChange}
                  required
                  maxLength={255}
                  placeholder="usuário ou e-mail"
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>
                  Senha {editingCard ? '(em branco = manter)' : '*'}
                </label>
                <input
                  name="password"
                  type="password"
                  value={cardForm.password}
                  onChange={handleCardChange}
                  required={!editingCard}
                  placeholder="••••••••"
                  autoComplete="new-password"
                  style={inputStyle}
                />
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '13px',
                  cursor: 'pointer',
                }}
              >
                <input
                  name="is_active"
                  type="checkbox"
                  checked={cardForm.is_active ?? true}
                  onChange={handleCardChange}
                />
                Cartão ativo
              </label>
            </div>

            <div
              style={{
                background: '#e6f7ff',
                border: '1px solid #91d5ff',
                borderRadius: '6px',
                padding: '10px 14px',
                marginBottom: '16px',
                fontSize: '12px',
                color: '#0050b3',
              }}
            >
              A senha é armazenada <strong>criptografada</strong> no backend e
              nunca aparece nas listagens. Use o botão de "olho" no cartão para
              revelá-la quando necessário.
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="submit"
                disabled={saving}
                style={{
                  ...primaryBtn,
                  background: saving ? '#ccc' : '#52c41a',
                  cursor: saving ? 'not-allowed' : 'pointer',
                }}
              >
                <Save size={14} />
                {saving ? 'Salvando…' : 'Salvar'}
              </button>
              <button type="button" onClick={cancelCardForm} style={secondaryBtn}>
                <X size={14} />
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Lista de cartões */}
      {cards.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            padding: '48px',
            color: '#888',
            border: '1px dashed #d9d9d9',
            borderRadius: '8px',
          }}
        >
          <CreditCard size={40} color="#ccc" style={{ marginBottom: '12px' }} />
          <p style={{ margin: 0 }}>Nenhum cartão de milhas cadastrado.</p>
          <p style={{ margin: '4px 0 0', fontSize: '13px' }}>
            Clique em "Novo Cartão" para começar.
          </p>
        </div>
      ) : (
        cards.map((card) => {
          const isOpen = expandedId === card.id;
          const bal = balances[card.id];
          return (
            <div
              key={card.id}
              style={{
                background: '#fff',
                border: '1px solid #e8e8e8',
                borderRadius: '8px',
                marginBottom: '16px',
                overflow: 'hidden',
                opacity: card.is_active ? 1 : 0.6,
              }}
            >
              {/* Linha do cartão */}
              <div
                style={{
                  padding: '14px 16px',
                  background: '#fafafa',
                  borderBottom: isOpen ? '1px solid #e8e8e8' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                }}
              >
                <button
                  onClick={() => toggleExpand(card.id)}
                  style={{ ...iconBtn, border: 'none', background: 'transparent' }}
                  title={isOpen ? 'Recolher' : 'Expandir'}
                >
                  {isOpen ? (
                    <ChevronDown size={18} />
                  ) : (
                    <ChevronRight size={18} />
                  )}
                </button>

                <span
                  style={{
                    background: '#e6f7ff',
                    color: '#1890ff',
                    padding: '3px 10px',
                    borderRadius: '10px',
                    fontSize: '12px',
                    fontWeight: 600,
                  }}
                >
                  {PROGRAM_LABEL[card.program]}
                </span>

                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: '14px' }}>
                    {card.name}
                  </div>
                  <div style={{ fontSize: '12px', color: '#888' }}>
                    {card.login}
                    {card.holder_name ? ` · ${card.holder_name}` : ''}
                  </div>
                </div>

                <span
                  style={{
                    background: card.is_active ? '#f6ffed' : '#f5f5f5',
                    color: card.is_active ? '#389e0d' : '#999',
                    padding: '2px 8px',
                    borderRadius: '10px',
                    fontSize: '11px',
                    fontWeight: 500,
                  }}
                >
                  {card.is_active ? 'Ativo' : 'Inativo'}
                </span>

                <div style={{ display: 'flex', gap: '6px' }}>
                  <button
                    title="Revelar login e senha"
                    onClick={() => handleRevealCredentials(card.id)}
                    style={iconBtn}
                  >
                    {revealed[card.id] ? (
                      <EyeOff size={13} color="#d46b08" />
                    ) : (
                      <Eye size={13} color="#1890ff" />
                    )}
                  </button>
                  <button
                    title={card.is_active ? 'Desativar' : 'Ativar'}
                    onClick={() => handleToggleActive(card)}
                    style={iconBtn}
                  >
                    {card.is_active ? (
                      <Power size={13} color="#389e0d" />
                    ) : (
                      <PowerOff size={13} color="#999" />
                    )}
                  </button>
                  <button
                    title="Editar"
                    onClick={() => handleEditCard(card)}
                    style={iconBtn}
                  >
                    <Edit2 size={13} color="#1890ff" />
                  </button>
                  <button
                    title="Desativar"
                    onClick={() => handleDeleteCard(card.id)}
                    style={{ ...iconBtn, border: '1px solid #ffccc7' }}
                  >
                    <Trash2 size={13} color="#cf1322" />
                  </button>
                </div>
              </div>

              {/* Credenciais reveladas */}
              {revealed[card.id] && (
                <div
                  style={{
                    padding: '10px 16px',
                    background: '#fffbe6',
                    borderBottom: '1px solid #ffe58f',
                    fontSize: '13px',
                    display: 'flex',
                    gap: '24px',
                  }}
                >
                  <span>
                    <strong>Login:</strong> {revealed[card.id].login}
                  </span>
                  <span>
                    <strong>Senha:</strong> {revealed[card.id].password}
                  </span>
                </div>
              )}

              {/* Painel expandido */}
              {isOpen && (
                <div style={{ padding: '16px' }}>
                  {/* Saldo */}
                  {bal && (
                    <div
                      style={{
                        display: 'flex',
                        gap: '16px',
                        marginBottom: '20px',
                      }}
                    >
                      {[
                        {
                          label: 'Compradas',
                          value: bal.totalPurchased,
                          color: '#1890ff',
                        },
                        {
                          label: 'Usadas',
                          value: bal.totalUsed,
                          color: '#d46b08',
                        },
                        {
                          label: 'Saldo',
                          value: bal.balance,
                          color: '#389e0d',
                        },
                      ].map((s) => (
                        <div
                          key={s.label}
                          style={{
                            flex: 1,
                            background: '#fafafa',
                            border: '1px solid #f0f0f0',
                            borderRadius: '8px',
                            padding: '12px 16px',
                          }}
                        >
                          <div style={{ fontSize: '12px', color: '#888' }}>
                            {s.label}
                          </div>
                          <div
                            style={{
                              fontSize: '20px',
                              fontWeight: 700,
                              color: s.color,
                            }}
                          >
                            {formatMiles(s.value)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Compras */}
                  <SectionHeader
                    title="Compras de Milhas"
                    onAdd={() => {
                      setShowPurchaseForm(
                        showPurchaseForm === card.id ? null : card.id,
                      );
                      setPurchaseForm(EMPTY_PURCHASE_FORM);
                    }}
                    addLabel="Nova Compra"
                  />

                  {showPurchaseForm === card.id && (
                    <form
                      onSubmit={(e) => handlePurchaseSubmit(e, card.id)}
                      style={subFormBox}
                    >
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr 1fr',
                          gap: '10px',
                          marginBottom: '10px',
                        }}
                      >
                        <div>
                          <label style={labelStyle}>Milhas compradas *</label>
                          <input
                            type="number"
                            min={1}
                            value={purchaseForm.milesPurchased}
                            onChange={(e) =>
                              setPurchaseForm((p) => ({
                                ...p,
                                milesPurchased: Number(e.target.value),
                              }))
                            }
                            required
                            style={inputStyle}
                          />
                        </div>
                        <div>
                          <label style={labelStyle}>Custo (R$)</label>
                          <input
                            type="number"
                            min={0}
                            step="0.01"
                            value={purchaseForm.cost ?? ''}
                            onChange={(e) =>
                              setPurchaseForm((p) => ({
                                ...p,
                                cost:
                                  e.target.value === ''
                                    ? undefined
                                    : Number(e.target.value),
                              }))
                            }
                            style={inputStyle}
                          />
                        </div>
                        <div>
                          <label style={labelStyle}>Data da compra *</label>
                          <input
                            type="date"
                            value={purchaseForm.purchasedAt}
                            onChange={(e) =>
                              setPurchaseForm((p) => ({
                                ...p,
                                purchasedAt: e.target.value,
                              }))
                            }
                            required
                            style={inputStyle}
                          />
                        </div>
                      </div>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr 1fr',
                          gap: '10px',
                          marginBottom: '10px',
                        }}
                      >
                        <div>
                          <label style={labelStyle}>Vencimento pagamento</label>
                          <input
                            type="date"
                            value={purchaseForm.paymentDueDate ?? ''}
                            onChange={(e) =>
                              setPurchaseForm((p) => ({
                                ...p,
                                paymentDueDate: e.target.value,
                              }))
                            }
                            style={inputStyle}
                          />
                        </div>
                        <div>
                          <label style={labelStyle}>Data de pagamento</label>
                          <input
                            type="date"
                            value={purchaseForm.paidAt ?? ''}
                            onChange={(e) =>
                              setPurchaseForm((p) => ({
                                ...p,
                                paidAt: e.target.value,
                              }))
                            }
                            style={inputStyle}
                          />
                        </div>
                        <div>
                          <label style={labelStyle}>Status pagamento</label>
                          <select
                            value={purchaseForm.paymentStatus}
                            onChange={(e) =>
                              setPurchaseForm((p) => ({
                                ...p,
                                paymentStatus: e.target
                                  .value as MileagePaymentStatus,
                              }))
                            }
                            style={inputStyle}
                          >
                            <option value="pending">Pendente</option>
                            <option value="paid">Pago</option>
                            <option value="overdue">Atrasado</option>
                          </select>
                        </div>
                      </div>

                      {/* Pagamento via PIX */}
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 2fr',
                          gap: '10px',
                          marginBottom: '10px',
                          alignItems: 'end',
                        }}
                      >
                        <div style={{ paddingBottom: '8px' }}>
                          <label
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              fontSize: '13px',
                              cursor: 'pointer',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={purchaseForm.payWithPix ?? false}
                              onChange={(e) =>
                                setPurchaseForm((p) => ({
                                  ...p,
                                  payWithPix: e.target.checked,
                                  pixKey: e.target.checked ? p.pixKey : '',
                                }))
                              }
                            />
                            Pagar via PIX
                          </label>
                        </div>
                        <div>
                          <label style={labelStyle}>
                            Chave PIX do cliente/fornecedor
                          </label>
                          <input
                            value={purchaseForm.pixKey ?? ''}
                            onChange={(e) =>
                              setPurchaseForm((p) => ({
                                ...p,
                                pixKey: e.target.value,
                              }))
                            }
                            disabled={!purchaseForm.payWithPix}
                            placeholder="CPF, e-mail, telefone ou chave aleatória"
                            maxLength={255}
                            style={{
                              ...inputStyle,
                              background: purchaseForm.payWithPix
                                ? '#fff'
                                : '#f5f5f5',
                            }}
                          />
                        </div>
                      </div>

                      <div style={{ marginBottom: '10px' }}>
                        <label style={labelStyle}>Observações</label>
                        <input
                          value={purchaseForm.notes ?? ''}
                          onChange={(e) =>
                            setPurchaseForm((p) => ({
                              ...p,
                              notes: e.target.value,
                            }))
                          }
                          maxLength={255}
                          style={inputStyle}
                        />
                      </div>
                      <SubFormActions
                        saving={saving}
                        onCancel={() => setShowPurchaseForm(null)}
                      />
                    </form>
                  )}

                  <table style={tableStyle}>
                    <thead>
                      <tr style={{ background: '#fafafa' }}>
                        <th style={thStyle}>Milhas</th>
                        <th style={thStyle}>Custo</th>
                        <th style={thStyle}>Preço/milheiro</th>
                        <th style={thStyle}>Compra</th>
                        <th style={thStyle}>Pagamento</th>
                        <th style={thStyle}>PIX</th>
                        <th style={thStyle}>Status</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(purchases[card.id] ?? []).length === 0 ? (
                        <tr>
                          <td
                            colSpan={8}
                            style={{
                              padding: '16px',
                              textAlign: 'center',
                              color: '#999',
                              fontSize: '13px',
                            }}
                          >
                            Nenhuma compra registrada.
                          </td>
                        </tr>
                      ) : (
                        (purchases[card.id] ?? []).map((p) => {
                          const c = PAYMENT_STATUS_COLORS[p.paymentStatus];
                          return (
                            <tr
                              key={p.id}
                              style={{
                                fontSize: '13px',
                                borderBottom: '1px solid #f0f0f0',
                              }}
                            >
                              <td style={{ padding: '10px 16px', fontWeight: 600 }}>
                                {formatMiles(p.milesPurchased)}
                              </td>
                              <td style={{ padding: '10px 16px' }}>
                                {formatPrice(p.cost)}
                              </td>
                              <td
                                style={{ padding: '10px 16px' }}
                                title="Preço médio pago por mil milhas (custo ÷ milhas × 1000)"
                              >
                                {formatPricePerThousand(
                                  p.cost,
                                  p.milesPurchased,
                                )}
                              </td>
                              <td style={{ padding: '10px 16px' }}>
                                {formatDate(p.purchasedAt)}
                              </td>
                              <td style={{ padding: '10px 16px' }}>
                                {p.paidAt
                                  ? formatDate(p.paidAt)
                                  : p.paymentDueDate
                                  ? `vence ${formatDate(p.paymentDueDate)}`
                                  : '—'}
                              </td>
                              <td style={{ padding: '10px 16px' }}>
                                {p.payWithPix ? (
                                  <span
                                    style={{
                                      background: '#e6fffb',
                                      color: '#08979c',
                                      padding: '2px 8px',
                                      borderRadius: '10px',
                                      fontSize: '11px',
                                      fontWeight: 500,
                                    }}
                                    title={p.pixKey || 'Sem chave informada'}
                                  >
                                    PIX
                                    {p.pixKey ? ` · ${p.pixKey}` : ''}
                                  </span>
                                ) : (
                                  '—'
                                )}
                              </td>
                              <td style={{ padding: '10px 16px' }}>
                                <span
                                  style={{
                                    background: c.bg,
                                    color: c.fg,
                                    padding: '2px 8px',
                                    borderRadius: '10px',
                                    fontSize: '11px',
                                    fontWeight: 500,
                                  }}
                                >
                                  {PAYMENT_STATUS_LABEL[p.paymentStatus]}
                                </span>
                              </td>
                              <td
                                style={{
                                  padding: '10px 16px',
                                  textAlign: 'right',
                                }}
                              >
                                <button
                                  title="Excluir"
                                  onClick={() =>
                                    handleDeletePurchase(p.id, card.id)
                                  }
                                  style={{
                                    ...iconBtn,
                                    border: '1px solid #ffccc7',
                                    marginLeft: 'auto',
                                  }}
                                >
                                  <Trash2 size={13} color="#cf1322" />
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>

                  {/* Usos (somente leitura — atrelamento feito na emissão) */}
                  <div style={{ marginTop: '20px' }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '10px',
                      }}
                    >
                      <h4
                        style={{ margin: 0, fontSize: '14px', fontWeight: 600 }}
                      >
                        Uso de Milhas (atrelado na emissão)
                      </h4>
                    </div>

                    <div
                      style={{
                        background: '#e6f7ff',
                        border: '1px solid #91d5ff',
                        borderRadius: '6px',
                        padding: '8px 12px',
                        marginBottom: '10px',
                        fontSize: '12px',
                        color: '#0050b3',
                      }}
                    >
                      O consumo de milhas é registrado na tela de{' '}
                      <strong>Emissão de Passagens</strong> (/emissions/:id),
                      marcando qual trecho foi emitido por milhas. Aqui a lista é
                      apenas para consulta.
                    </div>

                    <table style={tableStyle}>
                      <thead>
                        <tr style={{ background: '#fafafa' }}>
                          <th style={thStyle}>Milhas</th>
                          <th style={thStyle}>Trecho</th>
                          <th style={thStyle}>Ida/Volta</th>
                          <th style={thStyle}>Checkout</th>
                          <th style={thStyle}>Data</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(usages[card.id] ?? []).length === 0 ? (
                          <tr>
                            <td
                              colSpan={5}
                              style={{
                                padding: '16px',
                                textAlign: 'center',
                                color: '#999',
                                fontSize: '13px',
                              }}
                            >
                              Nenhum uso registrado.
                            </td>
                          </tr>
                        ) : (
                          (usages[card.id] ?? []).map((u) => (
                            <tr
                              key={u.id}
                              style={{
                                fontSize: '13px',
                                borderBottom: '1px solid #f0f0f0',
                              }}
                            >
                              <td
                                style={{ padding: '10px 16px', fontWeight: 600 }}
                              >
                                {formatMiles(u.milesUsed)}
                              </td>
                              <td style={{ padding: '10px 16px' }}>
                                {USAGE_LEG_LABEL[u.usageLeg]}
                              </td>
                              <td style={{ padding: '10px 16px' }}>
                                {u.isRoundTrip ? 'Sim' : 'Não'}
                              </td>
                              <td
                                style={{
                                  padding: '10px 16px',
                                  fontFamily: 'monospace',
                                  fontSize: '11px',
                                }}
                              >
                                {u.checkoutId ? (
                                  <span
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '4px',
                                    }}
                                  >
                                    <Plane size={11} color="#1890ff" />
                                    {u.checkoutId.slice(0, 8)}…
                                  </span>
                                ) : (
                                  '—'
                                )}
                              </td>
                              <td style={{ padding: '10px 16px' }}>
                                {formatDate(u.usedAt)}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

// ---------- sub-componentes ----------
const subFormBox: React.CSSProperties = {
  background: '#f8f9fa',
  border: '1px solid #e8e8e8',
  borderRadius: '8px',
  padding: '16px',
  marginBottom: '12px',
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  border: '1px solid #f0f0f0',
  borderRadius: '8px',
  overflow: 'hidden',
};

const SectionHeader: React.FC<{
  title: string;
  onAdd: () => void;
  addLabel: string;
}> = ({ title, onAdd, addLabel }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '10px',
    }}
  >
    <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 600 }}>{title}</h4>
    <button onClick={onAdd} style={{ ...secondaryBtn, padding: '6px 12px' }}>
      <Plus size={14} />
      {addLabel}
    </button>
  </div>
);

const SubFormActions: React.FC<{ saving: boolean; onCancel: () => void }> = ({
  saving,
  onCancel,
}) => (
  <div style={{ display: 'flex', gap: '8px' }}>
    <button
      type="submit"
      disabled={saving}
      style={{
        ...primaryBtn,
        padding: '7px 16px',
        background: saving ? '#ccc' : '#52c41a',
        cursor: saving ? 'not-allowed' : 'pointer',
      }}
    >
      <Save size={14} />
      {saving ? 'Salvando…' : 'Salvar'}
    </button>
    <button
      type="button"
      onClick={onCancel}
      style={{ ...secondaryBtn, padding: '7px 14px' }}
    >
      <X size={14} />
      Cancelar
    </button>
  </div>
);

export default MileageCards;
